import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects'
import _ from 'lodash'
import moment from 'moment'
import { saveAs } from 'file-saver'
import { store as reduxStore } from 'index'
import {
  listAuditLogs,
  getAuditLog,
  getAuditLogCatalog,
  exportAuditLogs,
  isAuditServiceError,
  getAuditErrorMessage,
  AuditLogCatalog,
  AuditLogEvent,
  AuditLogExportFormat,
  AuditLogListResponse,
  AuditLogQuery,
  AuditLogsApp,
  AuditServiceErrorBody,
} from 'services/auditLogs'
import { collaborators } from 'services/teams'
import { get as getApp } from 'services/apps'
import { checkIfFeatureRestricted, isAppAdmin, isAppOwner } from 'helpers'
import actions from './actions'
import { ActorDirectory, AuditLogsState } from './reducers'

type SagaWorker = (action: any) => any

export type AuditLogsDirection = 'first' | 'next' | 'prev' | 'current'

export interface ListAuditLogsPayload {
  direction: AuditLogsDirection
  query?: AuditLogQuery
}

interface PagingTarget {
  cursor: string | null
  cursorStack: (string | null)[]
  pageIndex: number
  query: AuditLogQuery
}

const getActiveApp = (): AuditLogsApp => reduxStore.getState().apps.active

export const selectAuditLogs = (state: { auditLogs: AuditLogsState }): AuditLogsState =>
  state.auditLogs

/**
 * A failed read can mean the viewer lost the role or the app lost the feature mid-session. Both answers
 * live on the app, so it is re-read and, when access is gone, handed to the page's own gates.
 */
export function* refreshAccessIfLost(): Generator<any, boolean, any> {
  const appId = getActiveApp()?.id
  if (!appId) return false
  try {
    const fresh = yield call(getApp, appId)
    const app = fresh?.data
    if (!app?.id || app.id !== appId) return false
    const canView = isAppOwner(app) || isAppAdmin(app)
    if (canView && !checkIfFeatureRestricted(app, 'audit_logs')) return false
    yield put({ type: 'apps/SET_STATE', payload: { active: app } })
    return true
  } catch (error) {
    return false
  }
}

export const selectActorDirectory = (state: { auditLogs: AuditLogsState }): ActorDirectory =>
  state.auditLogs.actorDirectory

/** Where a direction lands, or null when it is a no-op (no next page, already on the first). */
export function resolvePaging(
  state: AuditLogsState,
  direction: AuditLogsDirection,
  query?: AuditLogQuery,
): PagingTarget | null {
  const { cursorStack, pageIndex, nextCursor } = state
  switch (direction) {
    case 'first':
      return { cursor: null, cursorStack: [null], pageIndex: 0, query: query ?? state.query }
    case 'next':
      if (!nextCursor) return null
      return {
        cursor: nextCursor,
        cursorStack: [...cursorStack.slice(0, pageIndex + 1), nextCursor],
        pageIndex: pageIndex + 1,
        query: state.query,
      }
    case 'prev':
      if (pageIndex <= 0) return null
      return {
        cursor: cursorStack[pageIndex - 1] ?? null,
        cursorStack: cursorStack.slice(0, pageIndex),
        pageIndex: pageIndex - 1,
        query: state.query,
      }
    case 'current':
      return {
        cursor: cursorStack[pageIndex] ?? null,
        cursorStack,
        pageIndex,
        query: state.query,
      }
    default:
      return null
  }
}

export function* LIST_AUDIT_LOGS({ payload }: { payload: ListAuditLogsPayload }) {
  const state: AuditLogsState = yield select(selectAuditLogs)
  const target = resolvePaging(state, payload?.direction, payload?.query)
  if (!target) return

  // A new first page commits its query up front, so stale rows never sit under the new filters.
  const isFirst = payload.direction === 'first'
  yield put({
    type: actions.SET_STATE,
    payload: isFirst
      ? {
          areAuditLogsLoading: true,
          auditLogsList: [],
          nextCursor: null,
          cursorStack: target.cursorStack,
          pageIndex: target.pageIndex,
          query: target.query,
        }
      : { areAuditLogsLoading: true },
  })

  let errorMessage: string
  try {
    const result: AuditLogListResponse | AuditServiceErrorBody = yield call(
      listAuditLogs,
      getActiveApp(),
      { ...target.query, cursor: target.cursor },
    )
    if (!isAuditServiceError(result)) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          auditLogsList: result?.data ?? [],
          nextCursor: result?.nextCursor ?? null,
          auditLogsError: null,
          cursorStack: target.cursorStack,
          pageIndex: target.pageIndex,
          query: target.query,
          areAuditLogsLoading: false,
          hasLoadedOnce: true,
        },
      })
      return
    }
    errorMessage = getAuditErrorMessage(result)
  } catch (error) {
    errorMessage = getAuditErrorMessage(error)
  }

  // Paging keeps the previous list, so the position that produced it is put back; a first page keeps its query for Retry.
  const kept = isFirst ? target : state
  const lostAccess: boolean = yield call(refreshAccessIfLost)
  if (lostAccess) {
    yield put({ type: actions.SET_STATE, payload: { areAuditLogsLoading: false } })
    return
  }
  window.triggerNotification?.(errorMessage, 'error')
  yield put({
    type: actions.SET_STATE,
    payload: {
      auditLogsError: errorMessage,
      cursorStack: kept.cursorStack,
      pageIndex: kept.pageIndex,
      query: kept.query,
      areAuditLogsLoading: false,
      hasLoadedOnce: true,
    },
  })
}

// The catalog only feeds the filter options, so a failure degrades silently to no options.
export function* FETCH_AUDIT_LOG_CATALOG() {
  yield put({ type: actions.SET_STATE, payload: { isCatalogLoading: true } })
  let catalog: AuditLogCatalog['sections'] = []
  try {
    const result: AuditLogCatalog | AuditServiceErrorBody = yield call(
      getAuditLogCatalog,
      getActiveApp(),
    )
    if (!isAuditServiceError(result) && Array.isArray(result?.sections)) {
      catalog = result.sections
    }
  } catch (error) {
    catalog = []
  }
  yield put({ type: actions.SET_STATE, payload: { catalog, isCatalogLoading: false } })
}

interface CollaboratorRow {
  email?: unknown
  name?: unknown
}

export interface TeamActor {
  email: string
  name: string
}

// The list normally sits in `data`; nested arrays are read too in case the payload is grouped.
export function toTeamActors(response: unknown): TeamActor[] {
  const data = (response as { data?: unknown })?.data
  let rows: CollaboratorRow[] = []
  if (Array.isArray(data)) {
    rows = data
  } else if (data && typeof data === 'object') {
    rows = Object.values(data)
      .filter(Array.isArray)
      .flat()
  }
  return rows
    .filter(
      (row): row is CollaboratorRow & { email: string } =>
        typeof row?.email === 'string' && row.email !== '',
    )
    .map(row => ({ email: row.email, name: typeof row.name === 'string' ? row.name.trim() : '' }))
}

// Keyed by lower-cased email because audit events and the team list may differ in case.
export function toActorDirectory(actors: TeamActor[]): ActorDirectory {
  return actors.reduce<ActorDirectory>((directory, { email, name }) => {
    const key = email.toLowerCase()
    if (!directory[key]?.name) directory[key] = { name }
    return directory
  }, {})
}

interface CollaboratorsPage {
  error?: unknown
  meta?: { pagination?: { current_page?: number; total_pages?: number } }
}

export const MAX_ACTOR_PAGES = 20

// The collaborators endpoint pages at 10 rows, so every page is read to list the whole team.
export function* FETCH_AUDIT_LOG_ACTORS() {
  const app = getActiveApp()
  let actors: TeamActor[] = []
  try {
    for (let page = 1; page <= MAX_ACTOR_PAGES; page += 1) {
      const result: CollaboratorsPage = yield call(collaborators, app, page, '')
      if (result?.error) break
      actors = actors.concat(toTeamActors(result))
      const totalPages = Number(result?.meta?.pagination?.total_pages ?? 1)
      if (!(page < totalPages)) break
    }
  } catch (error) {
    // Keep the members already read; the filter still works with a partial list.
  }
  yield put({
    type: actions.SET_STATE,
    payload: {
      actorOptions: _.uniq(actors.map(actor => actor.email)),
      actorDirectory: toActorDirectory(actors),
    },
  })
}

const AUDIT_NOT_FOUND_CODE = 'NOT_FOUND'

// Only the audit service's NOT_FOUND means the event is gone; an outage or auth failure must not read as "not found".
export function* FETCH_AUDIT_LOG({ payload }: { payload: { externalId: string } }) {
  yield put({
    type: actions.SET_STATE,
    payload: {
      selectedAuditLog: null,
      isAuditLogLoading: true,
      auditLogNotFound: false,
      auditLogError: null,
    },
  })
  let event: AuditLogEvent | null = null
  let notFound = false
  let errorMessage: string | null = null
  try {
    const result: AuditLogEvent | AuditServiceErrorBody = yield call(
      getAuditLog,
      getActiveApp(),
      payload.externalId,
    )
    if (!isAuditServiceError(result)) {
      event = result?.externalId ? result : null
      notFound = !event
    } else if (result.error.code === AUDIT_NOT_FOUND_CODE) {
      notFound = true
    } else {
      errorMessage = getAuditErrorMessage(result)
    }
  } catch (error) {
    errorMessage = getAuditErrorMessage(error)
  }
  if (errorMessage) {
    // Access can be lost while the page is open, and then the gate belongs on screen, not a toast.
    const lostAccess: boolean = yield call(refreshAccessIfLost)
    if (lostAccess) {
      yield put({ type: actions.SET_STATE, payload: { isAuditLogLoading: false } })
      return
    }
    window.triggerNotification?.(errorMessage, 'error')
  }
  yield put({
    type: actions.SET_STATE,
    payload: {
      selectedAuditLog: event,
      isAuditLogLoading: false,
      auditLogNotFound: notFound,
      auditLogError: errorMessage,
    },
  })
}

export function* EXPORT_AUDIT_LOGS({ payload }: { payload: { format: AuditLogExportFormat } }) {
  const state: AuditLogsState = yield select(selectAuditLogs)
  if (state.isExporting) return

  yield put({ type: actions.SET_STATE, payload: { isExporting: true } })
  const app = getActiveApp()
  const { format } = payload
  try {
    const result: Blob | AuditServiceErrorBody = yield call(
      exportAuditLogs,
      app,
      format,
      state.query,
    )
    if (result instanceof Blob) {
      const fileName = `audit-logs-${app.id}-${moment().format('YYYYMMDD-HHmm')}.${format}`
      yield call(saveAs, result, fileName)
    } else {
      window.triggerNotification?.(getAuditErrorMessage(result, 'Export failed'), 'error')
    }
  } catch (error) {
    window.triggerNotification?.(getAuditErrorMessage(error, 'Export failed'), 'error')
  } finally {
    yield put({ type: actions.SET_STATE, payload: { isExporting: false } })
  }
}

export default function* auditLogsSaga() {
  yield all([
    // Latest-only: paging and filter changes can otherwise resolve out of order.
    takeLatest(actions.LIST_AUDIT_LOGS, LIST_AUDIT_LOGS as SagaWorker),
    takeLatest(actions.FETCH_AUDIT_LOG_CATALOG, FETCH_AUDIT_LOG_CATALOG as SagaWorker),
    takeLatest(actions.FETCH_AUDIT_LOG_ACTORS, FETCH_AUDIT_LOG_ACTORS as SagaWorker),
    takeLatest(actions.FETCH_AUDIT_LOG, FETCH_AUDIT_LOG as SagaWorker),
    // Leading-only: a repeat click must not cancel the download already in flight.
    takeLeading(actions.EXPORT_AUDIT_LOGS, EXPORT_AUDIT_LOGS as SagaWorker),
  ])
}
